/**
 * Web-only review shortcuts. RN `Pressable` has no arrow-key navigation, so
 * the hook listens on `document` and ignores keys typed into fields.
 * Handlers are read through a ref so the listener subscribes once.
 */
import { useEffect, useRef } from 'react';
import { Platform } from 'react-native';

export interface SofKeyboardHandlers {
  enabled: boolean;
  onMove: (delta: 1 | -1) => void;
  onPick: (index: number) => void;
  onConfirm: () => void;
  onToggleEvidence: () => void;
  onTranscript: () => void;
}

const EDITABLE = new Set(['INPUT', 'TEXTAREA', 'SELECT']);

export function useSofKeyboard(handlers: SofKeyboardHandlers) {
  const latest = useRef(handlers);
  latest.current = handlers;
  const { enabled } = handlers;
  useEffect(() => {
    if (Platform.OS !== 'web' || !enabled || typeof document === 'undefined') return undefined;
    const listener = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      if (target && (EDITABLE.has(target.tagName) || target.isContentEditable)) return;
      // A focused RNW Pressable (div[role=button]) handles Enter itself; don't double-fire.
      if (event.key === 'Enter' && target?.closest?.('[role="button"],button,a')) return;
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      const current = latest.current;
      switch (event.key) {
        case 'ArrowDown': case 'j': current.onMove(1); break;
        case 'ArrowUp': case 'k': current.onMove(-1); break;
        case 'Enter': current.onConfirm(); break;
        case 'e': case 'E': current.onToggleEvidence(); break;
        case 't': case 'T': current.onTranscript(); break;
        default:
          if (/^[1-5]$/.test(event.key)) { current.onPick(Number(event.key) - 1); break; }
          return;
      }
      event.preventDefault();
    };
    document.addEventListener('keydown', listener);
    return () => document.removeEventListener('keydown', listener);
  }, [enabled]);
}
